import { useTheme } from '@mui/material';

import type { IConfirmDialogColorResult, IConfirmDialogDescription, IGeneratedDefaults } from './types';
import { ConfirmDialogType } from './types';

export const useConfirmDialogColor = (type?: ConfirmDialogType): IConfirmDialogColorResult => {
  const theme = useTheme();

  let colorName: 'error' | 'warning' | 'primary';

  switch (type) {
    case ConfirmDialogType.Delete:
    case ConfirmDialogType.Error:
      colorName = 'error';
      break;

    case ConfirmDialogType.SoftDelete:
    case ConfirmDialogType.Warning:
    case ConfirmDialogType.Edit:
      colorName = 'warning';
      break;

    case ConfirmDialogType.Confirm:
    default:
      colorName = 'primary';
      break;
  }

  const paletteColor = theme.palette[colorName];

  return {
    name: colorName,
    hex: paletteColor?.main || theme.palette.primary.main,
  };
};

export const getConfirmDialogTyped = ({
  type,
  entityType: userEntityType = 'entity',
  entityName = 'item',
  description,
}: {
  type: ConfirmDialogType;
  entityType?: string;
  entityName?: string;
  description?: IConfirmDialogDescription;
}): IGeneratedDefaults => {
  const entityType = userEntityType.toLowerCase();

  switch (type) {
    case ConfirmDialogType.Delete:
      return {
        title: `Delete ${entityType}?`,
        description: description ?? {
          part1: `Are you sure you want to delete ${entityType}:`,
          part2: '?',
          part3: 'This action cannot be undone.',
        },
        confirmLabel: `Delete ${entityType}`,
        cancelLabel: 'Keep',
        entityName,
      };

    case ConfirmDialogType.SoftDelete:
      return {
        title: `Delete ${entityType}?`,
        description: description ?? {
          part1: `Are you sure you want to delete ${entityType}:`,
          part2: '?',
          part3: 'Warning: This action may break the related workflow.',
        },
        confirmLabel: `Delete ${entityType}`,
        cancelLabel: 'Keep',
        entityName,
      };

    case ConfirmDialogType.Edit:
      return {
        title: `Edit promoted ${entityType}?`,
        description: description ?? {
          part1: 'Are you sure you want to edit:',
          part2: '?',
          part3: `This action will create a new un-promoted version of ${entityType}.`,
        },
        confirmLabel: `Edit ${entityType}`,
        cancelLabel: 'Cancel',
        entityName,
      };

    default:
      return {
        title: `${entityType}?`,
        description: description ?? { part1: 'Are you sure you want to continue action', part2: '?', part3: '' },
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        entityName,
      };
  }
};
